import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { CK_ROUTES, apiMessage, type CreateStockTransferBody, type WarehouseRef } from '../centralKitchenApi';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  defaultBranchId?: string | null;
}

interface ItemOption {
  id: string;
  name: string;
  sku?: string | null;
  unitId?: string | null;
}

interface LineDraft {
  inventoryItemId: string;
  quantity: string;
  notes: string;
}

const EMPTY_LINE: LineDraft = { inventoryItemId: '', quantity: '', notes: '' };

/**
 * `POST /stock-transfers` — a DRAFT. Moves no stock; only `dispatch` does
 * that. Two warehouse pickers rather than one branch-scoped list, because a
 * transfer's whole point is that its two ends can be in different branches —
 * that is the journey ADR-0015 gives a middle to.
 */
export default function CreateTransferDialog({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);

  const warehouses = useApi<unknown>(open ? CK_ROUTES.warehouses : null, { isActive: true, limit: 200 });
  const { rows: warehouseRows } = rowsOf<WarehouseRef>(warehouses.data);

  const items = useApi<unknown>(open ? CK_ROUTES.items : null, { limit: 300 });
  const { rows: itemRows } = rowsOf<ItemOption>(items.data);

  const validLines = useMemo(
    () => lines.filter((l) => l.inventoryItemId && l.quantity.trim()),
    [lines],
  );

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const addLine = () => setLines((ls) => [...ls, { ...EMPTY_LINE }]);
  const removeLine = (index: number) => setLines((ls) => ls.filter((_, i) => i !== index));

  const reset = () => {
    setFromWarehouseId('');
    setToWarehouseId('');
    setNotes('');
    setLines([{ ...EMPTY_LINE }]);
  };

  const submit = async () => {
    if (!fromWarehouseId || !toWarehouseId || validLines.length === 0) return;
    setBusy(true);
    try {
      const body: CreateStockTransferBody = {
        fromWarehouseId,
        toWarehouseId,
        notes: notes.trim() || undefined,
        lines: validLines.map((l) => ({ inventoryItemId: l.inventoryItemId, quantity: l.quantity.trim(), notes: l.notes.trim() || undefined })),
      };
      const { data } = await api.post<{ id: string }>(CK_ROUTES.transfers, body);
      toast.success(t('centralKitchen.transfers.created'));
      reset();
      onCreated(data.id);
    } catch (e) {
      toast.error(apiMessage(e, t('centralKitchen.states.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('centralKitchen.transfers.new')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !fromWarehouseId || !toWarehouseId || validLines.length === 0}
          >
            {t('centralKitchen.transfers.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('centralKitchen.transfers.columns.from')} required controlId="ck-create-from">
            <Select value={fromWarehouseId} onChange={(e) => setFromWarehouseId(e.target.value)}>
              <option value="">{t('centralKitchen.transfers.chooseWarehouse')}</option>
              {warehouseRows.map((w) => (
                <option key={w.id} value={w.id} disabled={w.id === toWarehouseId}>
                  {w.name} ({w.code})
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('centralKitchen.transfers.columns.to')} required controlId="ck-create-to">
            <Select value={toWarehouseId} onChange={(e) => setToWarehouseId(e.target.value)}>
              <option value="">{t('centralKitchen.transfers.chooseWarehouse')}</option>
              {warehouseRows.map((w) => (
                <option key={w.id} value={w.id} disabled={w.id === fromWarehouseId}>
                  {w.name} ({w.code})
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">{t('centralKitchen.transfers.lines')}</p>
          {lines.map((line, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  value={line.inventoryItemId}
                  onChange={(e) => setLine(i, { inventoryItemId: e.target.value })}
                  aria-label={t('centralKitchen.transfers.columns.item')}
                >
                  <option value="">{t('centralKitchen.transfers.chooseItem')}</option>
                  {itemRows.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.name} {it.sku ? `(${it.sku})` : ''}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-32">
                <Input
                  inputMode="decimal"
                  value={line.quantity}
                  onChange={(e) => setLine(i, { quantity: e.target.value })}
                  placeholder={t('centralKitchen.transfers.quantityPlaceholder')}
                  aria-label={t('centralKitchen.transfers.columns.quantity')}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeLine(i)}
                disabled={lines.length === 1}
                aria-label={t('common.remove')}
              >
                <Icon as={Trash2} size="sm" className="text-red-600 dark:text-red-400" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addLine}>
            <Icon as={Plus} size="sm" />
            {t('centralKitchen.transfers.addLine')}
          </Button>
        </div>

        <Field label={t('centralKitchen.transfers.notes')} controlId="ck-create-notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
